import { useContext, useState } from 'react'

import { getOperationName } from '@apollo/client/utilities'
import {
  Box,
  Tooltip,
  Button as MantineButton,
  Indicator,
  Menu,
  ActionIcon,
} from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'

import { Link, routes } from '@redwoodjs/router'
import { useQuery, useMutation } from '@redwoodjs/web'

import { useAuth } from 'src/auth'
import AdminContainer from 'src/components/AdminContainer/AdminContainer'
import Tag from 'src/components/DataDisplay/Tag/Tag'
import DeleteModal from 'src/components/Overlays/DeleteModal/DeleteModal'
import { toast } from 'src/components/Overlays/Toast/Toast'
import { useActivityLogSidebarContext } from 'src/context/ActivityLogSidebarContext'
import { useApplicantDrawerContext } from 'src/context/ApplicantDrawerContext'
import { useCurrentCompanyContext } from 'src/context/CurrentCompanyContext'
import { RecruitersContext } from 'src/context/RecruitersContext'
import {
  GET_APPLICANTS,
  GET_APPLICANT_BY_ID,
  UPDATE_APPLICANT,
  ARCHIVE_APPLICANT,
  UNARCHIVE_APPLICANT,
} from 'src/graphql/applicants.graphql'
import { UPDATE_APPLICATION } from 'src/graphql/application.graphql'
import { GET_SIDEBAR_CHECKLIST_STEPS } from 'src/graphql/steps.graphql'
import { LicenseClass } from 'src/graphql/types/licenses'
import { SEND_CALL } from 'src/graphql/voiceAgents.graphql'
import { useVerticalResize } from 'src/hooks/useVerticalResize/useVerticalResize'
import IconChecklist from 'src/icons/IconChecklist'
import IconDocument from 'src/icons/IconDocument'
import IconDocumentCheck from 'src/icons/IconDocumentCheck'
import IconDragHandle from 'src/icons/IconDragHandle'
import IconEmail from 'src/icons/IconEmail'
import IconInputField from 'src/icons/IconInputField'
import IconLink from 'src/icons/IconLink'
import IconList from 'src/icons/IconList'
import IconMessage from 'src/icons/IconMessage'
import IconNavigate from 'src/icons/IconNavigate'
import IconNotes from 'src/icons/IconNotes'
import IconPause from 'src/icons/IconPause'
import IconPhoneCall from 'src/icons/IconPhoneCall'
import IconPlay from 'src/icons/IconPlay'
import IconSwapArrows from 'src/icons/IconSwapArrows'
import IconThreeDots from 'src/icons/IconThreeDots'
import IconTrash from 'src/icons/IconTrash'
import { formatAddress } from 'src/lib/address.utils'
import { buildFullName, formatExperience } from 'src/lib/formatters'
import { buildMutationErrorMessage } from 'src/lib/mutationErrorMessage'
import {
  unformatPhoneNumber,
  formatInternationalPhoneNumber,
} from 'src/lib/phone.utils'
import { sortStepsByTemplate } from 'src/lib/stepOrder'
import { buildDriverLink, buildZipCodeMapsUrl } from 'src/lib/url.utils'

import ApplicantStageMenu from './components/ApplicantStageMenu'
import ApplicantStageRow from './components/ApplicantStageRow'
import ApplicantTags from './components/ApplicantTags'
import CustomFieldsTab from './components/CustomFieldsTab'
import EmailTab from './components/EmailTab'
import EmployeesAssignmentSectionWithPopUpMenu from './components/EmployeesAssignmentSectionWithPopUpMenu'
import JobListingsMenu from './components/JobListingsMenu'
import MergeApplicantModal from './components/MergeApplicantModal'
import Row from './components/Row/Row'
import SidebarActivityLogs from './components/SidebarActivityLogs'
import {
  SidebarHeaderSkeleton,
  ChecklistSkeleton,
} from './components/SidebarSkeletons'
import StepItem from './components/StepItem/StepItem'
import TaskTab from './components/TaskTab'
import VoiceAgentsMenu from './components/VoiceAgentsMenu'

const onError = () => {
  toast('Something went wrong, please try again.', 'error')
}

const CustomTab = ({
  selectedTab,
  value,
  label,
  icon: Icon,
  setSelectedTab,
  showIndicator = false,
}) => {
  const isSelected = selectedTab === value
  const isNotesIcon = value === 'NOTE'
  const isCustomFieldsIcon = value === 'CUSTOM_FIELDS'

  const iconElement = (
    <Icon
      className={`${isCustomFieldsIcon ? 'h-6 w-6' : 'h-4 w-4'} ${
        isNotesIcon
          ? isSelected
            ? 'fill-doubleNickel-gray-700'
            : 'fill-doubleNickel-gray-500'
          : `fill-none ${
              isSelected
                ? 'stroke-doubleNickel-gray-700'
                : 'stroke-doubleNickel-gray-500'
            }`
      }`}
    />
  )

  return (
    <Tooltip label={label} position="bottom" withArrow>
      <Box
        className={`flex flex-row items-center justify-center rounded p-2 hover:cursor-pointer hover:bg-doubleNickel-gray-50 ${
          isSelected && 'bg-doubleNickel-gray-100'
        }`}
        onClick={() => setSelectedTab(value)}
      >
        {showIndicator ? (
          <Indicator color="red" size={6} offset={2}>
            {iconElement}
          </Indicator>
        ) : (
          iconElement
        )}
      </Box>
    </Tooltip>
  )
}

const ApplicantSidebar = ({
  applicantId,
  showNavigation = false,
  onRegisterCall = () => {},
  onPhoneCall = () => {},
}: {
  applicantId: string
  showNavigation: boolean
  onRegisterCall?: (activityLog) => void
  onPhoneCall?: (applicant) => void
}) => {
  const { close } = useApplicantDrawerContext()
  const { currentUser } = useAuth()
  const recruiters = useContext(RecruitersContext)
  const currentCompany = useCurrentCompanyContext()
  const contextValue = useActivityLogSidebarContext()
  const [localSelectedTab, setLocalSelectedTab] = useState<string | null>(
    'HIRING_STEPS'
  )

  // Use context if available (ApplicantDetailsPage), otherwise use local state (ApplicantDrawer)
  const selectedTab = contextValue?.selectedTab ?? localSelectedTab
  const setSelectedTab = contextValue?.setSelectedTab ?? setLocalSelectedTab
  const { percent, containerRef, onDragStart } = useVerticalResize({
    initialPercent: 40,
    minPercent: 0,
    maxPercent: 98.5,
  })
  const { data, loading: loadingApplicant } = useQuery(GET_APPLICANT_BY_ID, {
    variables: {
      id: applicantId,
    },
    onError,
  })

  // The checklist subtree (steps → documents → documentIntelligence) is by
  // far the most expensive part of the sidebar, so it loads in parallel
  // instead of blocking the header — see GET_SIDEBAR_CHECKLIST_STEPS.
  const { data: stepsData, loading: loadingSteps } = useQuery(
    GET_SIDEBAR_CHECKLIST_STEPS,
    {
      variables: { applicantId },
      onError,
    }
  )

  const applicant = data?.applicant || null
  const checklistSteps = stepsData?.steps?.items ?? []
  const memberEmployees = applicant?.memberEmployees ?? []
  const memberEmployeeIds = memberEmployees.map(
    (employee) => employee.employeeId
  )
  const leadEmployeeId = applicant?.recruiter?.employeeId ?? null
  const memberEmployeeOptions = recruiters.filter(
    (employee) =>
      employee.value !== leadEmployeeId &&
      !memberEmployeeIds.includes(employee.value)
  )

  const [updateApplicant] = useMutation(UPDATE_APPLICANT, {
    refetchQueries: [
      {
        query: GET_APPLICANT_BY_ID,
        variables: {
          id: applicantId,
        },
      },
      // Stage changes can add/remove checklist steps server-side
      getOperationName(GET_SIDEBAR_CHECKLIST_STEPS),
      getOperationName(GET_APPLICANTS), // Refetch the applicants list to update the table
    ],
    onCompleted: (data) => {
      // Check if there's a warning (validation failed)
      if (data?.updateApplicant?.warning) {
        toast(data.updateApplicant.warning, 'warning', { duration: 7000 })
        return
      }

      // Success - applicant was updated
      toast('Applicant updated successfully', 'success')
    },
    onError: (error) => {
      toast(
        buildMutationErrorMessage(
          error,
          'Something went wrong, please try again.'
        ),
        'error'
      )
    },
  })

  const [updateApplication] = useMutation(UPDATE_APPLICATION, {
    refetchQueries: [
      {
        query: GET_APPLICANT_BY_ID,
        variables: {
          id: applicantId,
        },
      },
      // Job listing changes can resync the checklist to a new template
      getOperationName(GET_SIDEBAR_CHECKLIST_STEPS),
      getOperationName(GET_APPLICANTS), // Refetch the applicants list to update the table
    ],
    onCompleted: () => {
      toast('Application updated successfully', 'success')
    },
    onError,
  })

  const [sendCall, { loading: calling }] = useMutation(SEND_CALL, {
    onCompleted: () => {
      toast('Call initiated successfully', 'success')
    },
    onError: (error) => {
      toast(error.message, 'error')
    },
  })

  const [
    archiveModalOpened,
    { open: openArchiveModal, close: closeArchiveModal },
  ] = useDisclosure(false)

  const [
    unarchiveModalOpened,
    { open: openUnarchiveModal, close: closeUnarchiveModal },
  ] = useDisclosure(false)

  const [mergeModalOpened, { open: openMergeModal, close: closeMergeModal }] =
    useDisclosure(false)

  const [archiveApplicant] = useMutation(ARCHIVE_APPLICANT, {
    refetchQueries: [
      { query: GET_APPLICANT_BY_ID, variables: { id: applicantId } },
      getOperationName(GET_APPLICANTS),
    ],
    onCompleted: () => {
      toast('Applicant archived', 'success')
      closeArchiveModal()
    },
    onError,
  })

  const [unarchiveApplicant] = useMutation(UNARCHIVE_APPLICANT, {
    refetchQueries: [
      { query: GET_APPLICANT_BY_ID, variables: { id: applicantId } },
      getOperationName(GET_APPLICANTS),
    ],
    onCompleted: () => {
      toast('Applicant unarchived', 'success')
      closeUnarchiveModal()
    },
    onError,
  })

  const isArchived = applicant?.status === 'ARCHIVED'

  const copyDriverLink = (kind: 'application' | 'review') => {
    navigator.clipboard.writeText(
      buildDriverLink({
        companySlug: currentCompany?.companySlug,
        applicationId: applicant.application?.applicationId,
        step: kind === 'review' ? 'review' : undefined,
      })
    )
    toast(
      `${
        kind === 'review' ? 'Review' : 'Application'
      } link copied to clipboard`,
      'success'
    )
  }

  const handleLeadEmployeeChange = (value) => {
    const nextMemberEmployeeIds = memberEmployeeIds.filter(
      (employeeId) => employeeId !== value
    )
    updateApplicant({
      variables: {
        id: applicantId,
        input: {
          recruiterId: value,
          ...(nextMemberEmployeeIds.length !== memberEmployeeIds.length && {
            memberEmployeeIds: nextMemberEmployeeIds,
          }),
        },
      },
    })
  }

  const handleMemberEmployeeChange = (nextMemberIds) => {
    updateApplicant({
      variables: {
        id: applicantId,
        input: {
          memberEmployeeIds: nextMemberIds,
        },
      },
    })
  }

  const handleJobListingChange = (jobListingId, trackingLinkId = null) => {
    if (applicant?.application?.applicationId && trackingLinkId) {
      updateApplication({
        variables: {
          id: applicant.application.applicationId,
          input: {
            jobListingId,
            trackingLinkId,
          },
        },
      })
    }
  }

  const handleApplicantStageChange = (input) => {
    updateApplicant({
      variables: {
        id: applicantId,
        input,
      },
    })
  }

  const handleCallClick = (voiceAgentId?: string) => {
    sendCall({
      variables: {
        applicantId: applicant.applicantId,
        companyId: applicant.companyId,
        employeeId: currentUser?.currentEmployeeId,
        ...(voiceAgentId && { voiceAgentId }),
      },
    })
  }

  const licenseInformation = (license) => {
    const licenseClassLabel =
      license.licenseClass == LicenseClass.NO_CDL
        ? 'No CDL'
        : `Class ${license.licenseClass}`

    const endorsements =
      license?.endorsements?.length > 0
        ? `(${license?.endorsements.join(', ')})`
        : ''

    return `${licenseClassLabel} ${endorsements}`
  }

  const fullName = applicant
    ? buildFullName({
        firstName: applicant.firstName,
        middleName: applicant.middleName,
        lastName: applicant.lastName,
      })
    : ''

  return (
    <div className="h-[100%] px-1">
      {loadingApplicant ? (
        <SidebarHeaderSkeleton />
      ) : applicant ? (
        <div
          ref={containerRef}
          className={`relative h-full ${isArchived ? 'opacity-70' : ''}`}
        >
          <div
            className="w-full space-y-3 overflow-auto px-1"
            style={{ height: `${percent}%` }}
          >
            <div className="flex w-full shrink-0 flex-col gap-2">
              <div className="flex w-full flex-col gap-1 text-lg font-semibold text-doubleNickel-gray-900">
                <div className="flex w-full flex-row items-center justify-between">
                  <div className="flex flex-row items-center gap-2">
                    {fullName}
                    {isArchived && (
                      <Tag size="xs" variant="gray" showCloseButton={false}>
                        Archived
                      </Tag>
                    )}
                  </div>
                  <div className="flex flex-row items-center gap-1">
                    <Menu shadow="md" position="bottom-end">
                      <Menu.Target>
                        <div className="flex">
                          <Tag
                            size="xs"
                            showCloseButton={false}
                            clickable={true}
                            variant="transparent"
                            className="gap-0 border border-dashed border-doubleNickel-gray-300 py-0 text-[11px] hover:bg-doubleNickel-gray-50"
                          >
                            <IconLink className="h-3 fill-none stroke-doubleNickel-gray-500" />
                            links
                          </Tag>
                        </div>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item
                          className="text-doubleNickel-gray-700"
                          leftSection={
                            <IconDocument className="h-3.5 w-3.5 fill-none stroke-doubleNickel-gray-700" />
                          }
                          onClick={() => copyDriverLink('application')}
                        >
                          Application link
                        </Menu.Item>
                        <Menu.Item
                          className="text-doubleNickel-gray-700"
                          leftSection={
                            <IconDocumentCheck className="h-3.5 w-3.5 fill-none stroke-doubleNickel-gray-700" />
                          }
                          onClick={() => copyDriverLink('review')}
                        >
                          Review link
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                    <Menu shadow="md" position="bottom-end">
                      <Menu.Target>
                        <ActionIcon variant="subtle" size="sm">
                          <IconThreeDots className="h-4 w-4 stroke-doubleNickel-gray-500" />
                        </ActionIcon>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item
                          className="text-doubleNickel-gray-700"
                          leftSection={
                            applicant.automationsEnabled ? (
                              <IconPause className="h-3.5 w-3.5 fill-none stroke-doubleNickel-gray-700" />
                            ) : (
                              <IconPlay className="h-3.5 w-3.5 fill-none stroke-doubleNickel-gray-700" />
                            )
                          }
                          onClick={() => {
                            updateApplicant({
                              variables: {
                                id: applicantId,
                                input: {
                                  automationsEnabled:
                                    !applicant.automationsEnabled,
                                },
                              },
                            })
                          }}
                        >
                          {applicant.automationsEnabled
                            ? 'Pause Automations'
                            : 'Resume Automations'}
                        </Menu.Item>
                        {!isArchived && (
                          <Menu.Item
                            className="text-doubleNickel-gray-700"
                            leftSection={
                              <IconSwapArrows className="h-3.5 w-3.5 fill-none stroke-doubleNickel-gray-700" />
                            }
                            onClick={openMergeModal}
                          >
                            Merge Applicant
                          </Menu.Item>
                        )}
                        {isArchived ? (
                          <Menu.Item
                            className="text-doubleNickel-gray-700"
                            leftSection={
                              <IconTrash className="h-3.5 w-3.5 fill-none stroke-doubleNickel-gray-700" />
                            }
                            onClick={openUnarchiveModal}
                          >
                            Unarchive Applicant
                          </Menu.Item>
                        ) : (
                          <Menu.Item
                            className="text-doubleNickel-gray-700"
                            leftSection={
                              <IconTrash className="h-3.5 w-3.5 fill-none stroke-doubleNickel-gray-700" />
                            }
                            onClick={openArchiveModal}
                          >
                            Archive Applicant
                          </Menu.Item>
                        )}
                      </Menu.Dropdown>
                    </Menu>
                  </div>
                </div>
                {applicant.mergeJobsAsDuplicate
                  ?.filter((j) => j.status === 'COMPLETED')
                  .map((job) => (
                    <Link
                      key={job.id}
                      to={routes.applicantDetails({
                        id: job.primaryApplicant.applicantId,
                        tab: 'application',
                      })}
                      className="text-xs font-normal text-doubleNickel-brand-500 hover:underline"
                    >
                      Merged into {buildFullName(job.primaryApplicant)}
                    </Link>
                  ))}

                <div className="flex w-full flex-row items-center gap-2">
                  <ApplicantStageMenu
                    applicant={applicant}
                    handleStageChange={handleApplicantStageChange}
                  />
                  {applicant?.automationsEnabled === false && (
                    <Tooltip
                      label="Automations like calls, texts, and emails are off for this applicant"
                      withArrow
                    >
                      <div>
                        <Tag
                          size="xs"
                          variant="warning"
                          showCloseButton={false}
                        >
                          Automations Off
                        </Tag>
                      </div>
                    </Tooltip>
                  )}
                </div>
              </div>
              <ApplicantStageRow applicant={applicant} />
            </div>

            <Row
              tooltipText="Job Listing"
              value={applicant.jobListing?.title ?? ''}
            >
              <JobListingsMenu
                handleJobListingChange={handleJobListingChange}
              />
            </Row>

            <Row tooltipText="Phone Number" value={''}>
              <div className="flex flex-row items-center gap-1 text-doubleNickel-brand-500 hover:cursor-pointer hover:underline">
                <Box
                  onClick={(event) => {
                    event.stopPropagation()
                    onPhoneCall({
                      applicantId: applicant.applicantId,
                      applicantFullName: buildFullName(applicant),
                      phone: unformatPhoneNumber(applicant.phone),
                    })
                  }}
                >
                  {formatInternationalPhoneNumber(applicant.phone)}
                </Box>
                <AdminContainer>
                  <VoiceAgentsMenu
                    onSelect={handleCallClick}
                    calling={calling}
                  />
                </AdminContainer>
              </div>
            </Row>

            {applicant.address && (
              <Row tooltipText="Address" value={''}>
                <div className="text-nowrap">
                  <a
                    href={
                      applicant.address.zipCode &&
                      buildZipCodeMapsUrl(applicant.address.zipCode)
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-doubleNickel-brand-500 hover:underline"
                  >
                    {formatAddress(applicant.address)}
                  </a>
                </div>
              </Row>
            )}

            {applicant.application?.license?.licenseClass && (
              <Row
                tooltipText="CDL Experience"
                value={licenseInformation(applicant.application.license)}
              />
            )}

            {applicant.yearsOfExperience !== null &&
              applicant.yearsOfExperience !== undefined && (
                <Row
                  tooltipText="Experience"
                  value={formatExperience(applicant.yearsOfExperience)}
                />
              )}

            {applicant.application?.accidents &&
              applicant.application.accidents.length > 0 && (
                <Row
                  tooltipText="Accidents & Violations"
                  value={`${
                    applicant.application?.accidents?.length ?? 0
                  } event(s) registered`}
                />
              )}

            <EmployeesAssignmentSectionWithPopUpMenu
              applicant={applicant}
              recruiters={recruiters}
              memberEmployees={memberEmployees}
              memberEmployeeIds={memberEmployeeIds}
              memberEmployeeOptions={memberEmployeeOptions}
              onLeadEmployeeChange={handleLeadEmployeeChange}
              onMemberEmployeeChange={handleMemberEmployeeChange}
            />
            <ApplicantTags applicant={applicant} />
          </div>

          {/* Drag handle */}
          <div
            className="group absolute left-0 z-10 flex h-3 w-full cursor-row-resize items-center justify-center transition-colors hover:bg-doubleNickel-gray-100"
            style={{ top: `${percent}%` }}
            onMouseDown={onDragStart}
          >
            <div className="absolute left-0 right-0 h-[1px] bg-doubleNickel-gray-200" />
            <IconDragHandle className="relative z-10 h-3 w-5 bg-white stroke-doubleNickel-gray-400 group-hover:bg-doubleNickel-gray-100" />
          </div>

          <div
            className="flex w-full flex-col gap-3 overflow-auto px-1 pt-5"
            style={{ height: `${100 - percent}%` }}
          >
            <div className="flex w-full shrink-0 flex-row gap-1 overflow-auto">
              <CustomTab
                selectedTab={selectedTab}
                value="HIRING_STEPS"
                label="Checklist"
                icon={IconList}
                setSelectedTab={setSelectedTab}
              />
              <CustomTab
                selectedTab={selectedTab}
                value="CALL"
                label={
                  applicant.isCommunicationOptIn === false
                    ? 'Calls (Opted Out)'
                    : 'Calls'
                }
                icon={IconPhoneCall}
                setSelectedTab={setSelectedTab}
                showIndicator={applicant.isCommunicationOptIn === false}
              />
              <CustomTab
                selectedTab={selectedTab}
                value="SMS"
                label={
                  applicant.isCommunicationOptIn === false
                    ? 'Texts (Opted Out)'
                    : 'Texts'
                }
                icon={IconMessage}
                setSelectedTab={setSelectedTab}
                showIndicator={applicant.isCommunicationOptIn === false}
              />
              <CustomTab
                selectedTab={selectedTab}
                value="EMAIL"
                label="Emails"
                icon={IconEmail}
                setSelectedTab={setSelectedTab}
              />
              <CustomTab
                selectedTab={selectedTab}
                value="NOTE"
                label="Notes"
                icon={IconNotes}
                setSelectedTab={setSelectedTab}
              />
              <CustomTab
                selectedTab={selectedTab}
                value="TASK"
                label="Tasks"
                icon={IconChecklist}
                setSelectedTab={setSelectedTab}
              />
              <CustomTab
                selectedTab={selectedTab}
                value="CUSTOM_FIELDS"
                label="Custom Fields"
                icon={IconInputField}
                setSelectedTab={setSelectedTab}
              />
            </div>
            {selectedTab == 'HIRING_STEPS' &&
              (loadingSteps ? (
                <ChecklistSkeleton />
              ) : (
                checklistSteps.length > 0 && (
                  <div className="flex flex-1 flex-col gap-1 overflow-auto px-1">
                    {sortStepsByTemplate(
                      checklistSteps,
                      applicant.jobListing
                    ).map((step) => (
                      <StepItem
                        key={step.stepId}
                        step={step}
                        applicantId={applicant.applicantId}
                      />
                    ))}
                  </div>
                )
              ))}
            {(selectedTab === 'NOTE' ||
              selectedTab === 'SMS' ||
              selectedTab == 'CALL') && (
              <div className="flex flex-1 flex-col gap-4 overflow-hidden">
                <SidebarActivityLogs
                  applicant={applicant}
                  communication={selectedTab}
                  onRegisterCall={onRegisterCall}
                />
              </div>
            )}
            {selectedTab === 'TASK' && <TaskTab applicant={applicant} />}
            {selectedTab === 'EMAIL' && <EmailTab applicant={applicant} />}
            {selectedTab === 'CUSTOM_FIELDS' && (
              <CustomFieldsTab applicant={applicant} />
            )}
            {showNavigation && (
              <MantineButton
                classNames={{
                  root: 'bg-doubleNickel-brand-500 text-white text-xs rounded-lg mt-auto min-h-8 hover:bg-doubleNickel-brand-700',
                }}
                component={Link}
                to={routes.applicantDetails({
                  id: applicant.applicantId,
                  tab: 'application',
                })}
                target="_blank"
                rel="noopener noreferrer"
                rightSection={
                  <IconNavigate className="h-4 fill-none stroke-white" />
                }
                onClick={close}
              >
                Go to applicant
              </MantineButton>
            )}
          </div>
        </div>
      ) : (
        <div className="flex flex-1 flex-col items-center justify-center text-sm text-doubleNickel-gray-500">
          Applicant not found
        </div>
      )}
      {archiveModalOpened && (
        <DeleteModal
          opened={archiveModalOpened}
          close={closeArchiveModal}
          title="Archive Applicant"
          description="Archiving this applicant will remove them from search results and the applicant list."
          onDelete={() => archiveApplicant({ variables: { id: applicantId } })}
        />
      )}
      {unarchiveModalOpened && (
        <DeleteModal
          opened={unarchiveModalOpened}
          close={closeUnarchiveModal}
          title="Unarchive Applicant"
          description="This applicant will be restored to the applicant list and search results."
          onDelete={() =>
            unarchiveApplicant({ variables: { id: applicantId } })
          }
        />
      )}
      {mergeModalOpened && (
        <MergeApplicantModal
          opened={mergeModalOpened}
          close={closeMergeModal}
          applicantId={applicantId}
          applicantName={fullName}
        />
      )}
    </div>
  )
}

export default ApplicantSidebar
